import React, { useId, useRef } from "react";
import "./ui.css";

/**
 * Toggle – form-switch s popiskem (titulek + popis na druhém řádku),
 * volitelně zamčený s důvodem (ikona zámku + text).
 * YesNoToggle – dvojice tlačítek Ano / Ne.
 * ChoiceGroup – 3 a více voleb (např. typ dokladu), volitelně v pevném počtu sloupců.
 *
 * YesNoToggle a ChoiceGroup se čtečkám hlásí jako radiogroup s role="radio"
 * a aria-checked; šipky / Home / End přepínají volbu, Tab skupinu opouští
 * (roving tabindex). Čistě prezentační komponenty bez vedlejších efektů.
 */

export type ToggleSize = "md" | "lg";

export interface ToggleProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Druhý řádek pod titulkem (nikdy inline za pomlčkou – na mobilu se láme). */
  description?: string;
  disabled?: boolean;
  /** Důvod zamčení: přepínač je neaktivní a místo pouhého zešednutí ukáže zámek + text. */
  lockedReason?: string;
  className?: string;
}

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  /** Ikona z bootstrap-icons, např. "bi-card-text". */
  icon?: string;
  disabled?: boolean;
}

interface OptionGroupCommonProps {
  /** Otázka nad tlačítky; slouží i jako přístupný název skupiny. */
  label?: string;
  /** Vysvětlující text pod otázkou. */
  description?: string;
  /** Chybová hláška s role="alert". */
  error?: string;
  /** Hvězdička u otázky + aria-required na skupině. */
  required?: boolean;
  disabled?: boolean;
  /** lg = větší tlačítka (btn-lg). Výchozí md. */
  size?: ToggleSize;
  /** Základ odvozených id (label/desc/error); bez něj se vygeneruje. */
  id?: string;
  className?: string;
}

export interface YesNoToggleProps extends OptionGroupCommonProps {
  /** null = zatím nevybráno. */
  value: boolean | null;
  onChange: (value: boolean) => void;
  /** Popisky tlačítek [ano, ne]. Výchozí ["Ano", "Ne"]. */
  labels?: [string, string];
}

export interface ChoiceGroupProps<T extends string>
  extends OptionGroupCommonProps {
  options: ChoiceOption<T>[];
  /** null = zatím nevybráno. */
  value: T | null;
  onChange: (value: T) => void;
  /** Pevný počet sloupců; bez něj se tlačítka zalamují podle šířky (min. 7rem). */
  columns?: number;
}

/** Spojí třídy a vynechá prázdné hodnoty. */
const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(" ");

/** Spojí id pro aria-describedby; prázdný výsledek = undefined. */
const joinIds = (...ids: Array<string | false | null | undefined>): string | undefined =>
  ids.filter(Boolean).join(" ") || undefined;

/** "bi-x", "bi bi-x" i "x" → "bi bi-x" */
const iconClass = (icon: string): string => {
  const name = icon.trim().replace(/^bi\s+/, "");
  return name.indexOf("bi-") === 0 ? `bi ${name}` : `bi bi-${name}`;
};

// ─────────────────────────────────────────────────────────────
//  Toggle
// ─────────────────────────────────────────────────────────────

export const Toggle: React.FC<ToggleProps> = ({
  id,
  label,
  checked,
  onChange,
  description,
  disabled,
  lockedReason,
  className,
}) => {
  const locked = Boolean(lockedReason);
  const isDisabled = Boolean(disabled) || locked;
  const descId = `${id}-desc`;
  const lockId = `${id}-lock`;

  return (
    <div
      className={cx(
        "form-check form-switch lks-toggle",
        isDisabled && "lks-toggle--disabled",
        locked && "lks-toggle--locked",
        className
      )}
    >
      <input
        className="form-check-input"
        type="checkbox"
        role="switch"
        id={id}
        checked={checked}
        disabled={isDisabled}
        aria-describedby={joinIds(description && descId, lockedReason && lockId)}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label className="form-check-label lks-toggle__label" htmlFor={id}>
        <span className="lks-toggle__title">{label}</span>
        {description && (
          <span id={descId} className="lks-toggle__desc">
            {description}
          </span>
        )}
        {lockedReason && (
          <span id={lockId} className="lks-toggle__lock">
            <i className="bi bi-lock-fill" aria-hidden="true" />
            {lockedReason}
          </span>
        )}
      </label>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
//  Společná část skupin tlačítek
// ─────────────────────────────────────────────────────────────

interface RadioButtonsProps<T extends string> {
  options: ChoiceOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  disabled?: boolean;
  size?: ToggleSize;
  className?: string;
  style?: React.CSSProperties;
  labelledBy?: string;
  describedBy?: string;
  required?: boolean;
}

function RadioButtons<T extends string>({
  options,
  value,
  onChange,
  disabled,
  size,
  className,
  style,
  labelledBy,
  describedBy,
  required,
}: RadioButtonsProps<T>): JSX.Element {
  const groupRef = useRef<HTMLDivElement>(null);

  const enabled: number[] = [];
  options.forEach((o, i) => {
    if (!disabled && !o.disabled) enabled.push(i);
  });
  const selectedIdx = options.findIndex((o) => o.value === value);
  const selectedPos = enabled.indexOf(selectedIdx);
  // Roving tabindex: Tab přijde na vybranou volbu, jinak na první aktivní
  const tabIdx = selectedPos >= 0 ? selectedIdx : enabled.length ? enabled[0] : -1;

  const moveTo = (idx: number) => {
    onChange(options[idx].value);
    const group = groupRef.current;
    if (!group) return;
    const buttons = group.querySelectorAll<HTMLButtonElement>("button[role='radio']");
    const target = buttons[idx];
    if (target) target.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!enabled.length) return;
    const last = enabled.length - 1;
    let next = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      next = selectedPos >= 0 && selectedPos < last ? selectedPos + 1 : 0;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      next = selectedPos > 0 ? selectedPos - 1 : last;
    } else if (e.key === "Home") {
      next = 0;
    } else if (e.key === "End") {
      next = last;
    }
    if (next < 0) return;
    e.preventDefault();
    moveTo(enabled[next]);
  };

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      className={className}
      style={style}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-required={required || undefined}
      onKeyDown={onKeyDown}
    >
      {options.map((o, i) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={i === tabIdx ? 0 : -1}
            disabled={disabled || o.disabled}
            className={cx(
              "btn",
              selected ? "btn-primary" : "btn-outline-secondary",
              size === "lg" && "btn-lg"
            )}
            onClick={() => onChange(o.value)}
          >
            {o.icon && (
              <i
                className={cx(iconClass(o.icon), "lks-choice__icon")}
                aria-hidden="true"
              />
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

interface OptionGroupShellProps extends OptionGroupCommonProps {
  /** Třída obalu (lks-yesno / lks-choice). */
  variant: "lks-yesno" | "lks-choice";
  /** Vykreslí skupinu tlačítek s předanými aria vazbami. */
  children: (ids: { labelledBy?: string; describedBy?: string }) => React.ReactNode;
}

/** Obal skupiny: otázka, popis, tlačítka, chyba + odvozená id pro aria. */
const OptionGroupShell: React.FC<OptionGroupShellProps> = ({
  label,
  description,
  error,
  required,
  id,
  className,
  variant,
  children,
}) => {
  const autoId = useId();
  const baseId = id || autoId;
  const labelId = `${baseId}-label`;
  const descId = `${baseId}-desc`;
  const errorId = `${baseId}-error`;

  return (
    <div className={cx(variant, className)}>
      {label && (
        <div id={labelId} className="lks-choice__label">
          {label}
          {required && (
            <span className="lks-field__req" aria-hidden="true">
              *
            </span>
          )}
        </div>
      )}
      {description && (
        <p id={descId} className="lks-choice__desc">
          {description}
        </p>
      )}
      {children({
        labelledBy: label ? labelId : undefined,
        describedBy: joinIds(description && descId, error && errorId),
      })}
      {error && (
        <div
          id={errorId}
          className="invalid-feedback d-block lks-choice__error"
          role="alert"
        >
          {error}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
//  YesNoToggle
// ─────────────────────────────────────────────────────────────

const DEFAULT_YESNO_LABELS: [string, string] = ["Ano", "Ne"];

type YesNoValue = "yes" | "no";

export const YesNoToggle: React.FC<YesNoToggleProps> = ({
  value,
  onChange,
  labels = DEFAULT_YESNO_LABELS,
  required,
  disabled,
  size = "md",
  label,
  description,
  error,
  id,
  className,
}) => {
  const options: ChoiceOption<YesNoValue>[] = [
    { value: "yes", label: labels[0] },
    { value: "no", label: labels[1] },
  ];
  const current: YesNoValue | null = value === null ? null : value ? "yes" : "no";

  return (
    <OptionGroupShell
      variant="lks-yesno"
      label={label}
      description={description}
      error={error}
      required={required}
      id={id}
      className={className}
    >
      {(ids) => (
        <RadioButtons
          options={options}
          value={current}
          onChange={(v) => onChange(v === "yes")}
          disabled={disabled}
          size={size}
          required={required}
          className="btn-group lks-yesno__group"
          labelledBy={ids.labelledBy}
          describedBy={ids.describedBy}
        />
      )}
    </OptionGroupShell>
  );
};

// ─────────────────────────────────────────────────────────────
//  ChoiceGroup
// ─────────────────────────────────────────────────────────────

export function ChoiceGroup<T extends string>({
  options,
  value,
  onChange,
  columns,
  required,
  disabled,
  size = "md",
  label,
  description,
  error,
  id,
  className,
}: ChoiceGroupProps<T>): JSX.Element {
  const style =
    columns && columns > 0
      ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
      : undefined;

  return (
    <OptionGroupShell
      variant="lks-choice"
      label={label}
      description={description}
      error={error}
      required={required}
      id={id}
      className={className}
    >
      {(ids) => (
        <RadioButtons
          options={options}
          value={value}
          onChange={onChange}
          disabled={disabled}
          size={size}
          required={required}
          className="lks-choice__group"
          style={style}
          labelledBy={ids.labelledBy}
          describedBy={ids.describedBy}
        />
      )}
    </OptionGroupShell>
  );
}

export default Toggle;
